import { ThreadOrChannelChooser, asksThreadOrChannel, type ThreadChoice } from '../features/threads/ThreadOrChannelChooser';
import { memo, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent } from 'react';
import { categoryDisplayName, userDisplayName, visibleRows } from '../../core/channelListDerivation';
import type { ChannelListSection, DisplayChannel } from '../../core/channelListDisplayModels';
import { ChannelMarkRead } from '../../core/channelMarkRead';
import { RelativeTime } from '../../core/relativeTime';
import { SidebarAttention } from '../../core/sidebarAttention';
import { Icon } from '../../design/icons';
import { t, tf } from '../../i18n/i18n';
import { Avatar } from '../Avatar';
import { AttendanceChip } from '../features/attendance/AttendanceParts';
import type { SidebarModel } from '../models/sidebarModel';
import { filterSections } from '../models/sidebarModel';
import { useMinuteClock } from '../hooks';
import type { Destination } from '../router';
import { useThreadsBadge } from '../features/destinations/threadsBadge';
import { CategoryHeader, FoldedCategories, MuteConfirm, RowMuteItem, TeamStrip } from '../features/syncParity/SidebarParity';
import { OwnStatusEmoji } from '../features/settings/OwnStatusEmoji';
import { ChannelListTab } from '../../core/channelListTabs';
import { CallRowSlot } from '../features/calls/CallSurfaces';
import { HuddlePanel } from '../features/calls/Huddle';
import { AreaGrouping } from '../../core/areaGrouping';
import { ChannelMeta } from '../../core/channelMeta';
import { setAreaSectionCollapsed, useAreaPrefs } from '../features/areas/areaPrefs';
import { MoveToMenuItems } from '../features/nav/CategoryMenus';
import { openNav } from '../features/nav/navBus';
import { useWhatsAppLine } from '../features/whatsAppChat/WhatsAppChatDialog';
import { CatchUpChip } from '../features/extras/CatchUp';
import { CopyNameItems } from '../features/extras/copyName';
import './sidebarChrome.css';

const COMPACT = '(max-width: 760px)';

type NewKind = 'newConversation' | 'whatsAppChat' | 'messageArea' | 'browseChannels' | 'createChannel';

function useCompact(): boolean {
  const [compact, setCompact] = useState(() => window.matchMedia(COMPACT).matches);
  useEffect(() => {
    const mq = window.matchMedia(COMPACT);
    const on = () => setCompact(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return compact;
}

type PhoneTab = 'dms' | 'channels';

function storedPhoneTab(): PhoneTab {
  try {
    return localStorage.getItem('satori.phoneTab') === 'channels' ? 'channels' : 'dms';
  } catch {
    return 'dms';
  }
}

export interface SidebarProps {
  model: SidebarModel | null;
  teamId: string | null;
  selectedChannelId: string | null;
  activeDestination: Destination | null;
  onSelectTeam(teamId: string): void;
  onSelectChannel(channelId: string): void;
  onOpenDestination(d: 'threads' | 'activity' | 'saved' | 'drafts'): void;
  onSearchMessages(query: string): void;
  onNew(kind: NewKind): void;
  draftCount: number;
  /** A card shown above the footer (e.g. the notifications permission ask) — never over the list. */
  notice?: React.ReactNode;
  onToggleCategory(categoryId: string, collapsed: boolean): void;
  onMarkRead(channelId: string): void;
  onOpenProfile(anchor: DOMRect): void;
  query: string;
  onQuery(q: string): void;
  serverBase: string;
}

export function Sidebar(props: SidebarProps) {
  const { model } = props;
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [menu, setMenu] = useState<{ x: number; y: number; row: DisplayChannel } | null>(null);
  // A row whose newest message is a thread reply asks which door (features/threads, P029).
  const [threadChoice, setThreadChoice] = useState<ThreadChoice | null>(null);
  const [newMenu, setNewMenu] = useState<DOMRect | null>(null);
  // «Nuevo chat de WhatsApp» only on a WhatsApp line team (shared/API.md §15.4) — measured with the
  // team, not when the menu opens, so the entry is there on the menu's first frame.
  const whatsAppLine = useWhatsAppLine(props.teamId);
  const [confirmMute, setConfirmMute] = useState<DisplayChannel | null>(null);
  const threads = useThreadsBadge(props.teamId);
  const now = useMinuteClock();
  const searchRef = useRef<HTMLInputElement>(null);

  const compact = useCompact();
  const [phoneTab, setPhoneTabState] = useState<PhoneTab>(storedPhoneTab);
  const setPhoneTab = (tab: PhoneTab) => {
    setPhoneTabState(tab);
    try {
      localStorage.setItem('satori.phoneTab', tab);
    } catch {
      /* a per-viewer convenience */
    }
  };
  const areaPrefs = useAreaPrefs();
  // §46: channel types become sections BEFORE the filter, so a search for a type's name finds them;
  // their collapse state is this browser's own (kept with the área sections').
  const filtered = useMemo(
    () => (model ? filterSections(ChannelMeta.group(model.sections, model.channelMeta, areaPrefs.collapsedIds), props.query, unreadOnly) : []),
    [model, props.query, unreadOnly, areaPrefs],
  );
  // The phone splits Mensajes | Canales (Android/iOS, user 2026-10-09); a search spans both.
  const splitsList = compact && props.query.trim() === '';
  // «Agrupar por área» (shared/API.md §37): the DM category split by the other person's área, on both
  // the desktop list and the phone's Mensajes; their collapse state is this browser's own.
  const sections = useMemo(() => {
    const base = splitsList ? ChannelListTab.sections(filtered, phoneTab) : filtered;
    return AreaGrouping.group(base, { enabled: areaPrefs.groupByArea, collapsedIds: areaPrefs.collapsedIds });
  }, [filtered, splitsList, phoneTab, areaPrefs]);
  const toggleSection = (id: string, collapsed: boolean) => {
    if (AreaGrouping.isAreaSectionId(id) || ChannelMeta.isTypeSectionId(id)) setAreaSectionCollapsed(id, collapsed);
    else props.onToggleCategory(id, collapsed);
  };
  const tabBadges = useMemo(() => ChannelListTab.badges(model?.sections ?? []), [model]);
  const anyUnread = useMemo(() => !!model?.sections.some((s) => s.rows.some((r) => (r.isUnread || r.mentionCount > 0) && !r.isMuted)), [model]);
  const isSearching = props.query.trim() !== '';
  // Collapsed categories with nothing to surface fold into one row of pills (the Mac's sidebar).
  const folded = useMemo(() => SidebarAttention.foldedSectionIds(sections, isSearching), [sections, isSearching]);

  // ⌘K / Ctrl+K opens the recent-first switcher (ui/features/nav/NavHost.tsx); the field filters in place.

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    window.addEventListener('click', close);
    window.addEventListener('blur', close);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('blur', close);
    };
  }, [menu]);

  // One Tab stop for the whole list (listbox): the open conversation, else the first row; arrows move.
  const shownSections = sections.filter((sec) => !folded.has(sec.id));
  const allRowIds = shownSections.flatMap((sec) => visibleRows(sec.rows, { collapsed: sec.collapsed, isSearching, selectedId: props.selectedChannelId }).map((r) => r.id));
  const focusId = props.selectedChannelId && allRowIds.includes(props.selectedChannelId) ? props.selectedChannelId : allRowIds[0] ?? null;

  return (
    <nav className="sidebar" aria-label={t('channelList.title')}>
      <div className="wash" />
      {/* The header — the apps' TeamsHeaderStrip: the logos (two teams or more), the console gear
          for admins. On a phone it is Android's top bar: logos as the title, then "+" and the
          reader's own avatar (the profile lives up here, there is no footer). */}
      <header className="sidebar-head">
        {model && (model.teams.length > 1 || model.isAdmin) ? (
          <TeamStrip teams={model.teams} current={props.teamId} onSelect={props.onSelectTeam} isAdmin={model.isAdmin} serverBase={props.serverBase} />
        ) : (
          <h1 className="sidebar-title">
            {/* The team name opens the team sheet (Android's TeamSwitcherSheet; shared/API.md §41.6). */}
            <button type="button" className="nav-title-button" onClick={() => model && openNav({ kind: 'teamSheet' })} title={t('web.teams.switch')} aria-haspopup="dialog">
              {model?.teams.find((x) => x.team.id === props.teamId)?.team.displayName ?? t('channelList.title')}
              <Icon name="chevronDown" size={12} strokeWidth={2.2} />
            </button>
          </h1>
        )}
        {compact ? (
          <div className="sidebar-head-actions">
            <AttendanceChip compact />
            <button
              type="button"
              className="icon-button"
              title={t('mac.newMenu')}
              aria-label={t('mac.newMenu')}
              aria-haspopup="menu"
              onClick={(e) => {
                e.stopPropagation();
                setNewMenu(e.currentTarget.getBoundingClientRect());
              }}
            >
              <Icon name="plusCircle" size={21} />
            </button>
            {model?.me ? (
              <button type="button" className="sidebar-head-me" onClick={(e) => props.onOpenProfile(e.currentTarget.getBoundingClientRect())} title={t('mac.profileHelp')}>
                {/* Named by its hidden text, the face is decoration (the initials are not the name, P255). */}
                <span aria-hidden>
                  <Avatar userId={model.me.id} name={userDisplayName(model.me)} size={32} version={model.me.lastPictureUpdate} presence={model.myPresence} ring />
                </span>
                <span className="visually-hidden">{userDisplayName(model.me)}</span>
              </button>
            ) : null}
          </div>
        ) : (
          // Desktop: the reader's own company status, short, at the strip's trailing end — the
          // footer spent a whole row on it (user, 2026-10-26); the phone already shows it up here.
          <div className="sidebar-head-actions">
            <AttendanceChip compact />
          </div>
        )}
      </header>
      <div className="sidebar-search">
        <label className="search-field">
          <Icon name="search" size={15} />
          <input
            ref={searchRef}
            type="search"
            value={props.query}
            placeholder={t('mac.searchPlaceholder')}
            onChange={(e) => props.onQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') props.onQuery('');
              if (e.key === 'Enter') {
                const first = sections[0]?.rows[0];
                if (first) {
                  props.onSelectChannel(first.id);
                  props.onQuery('');
                }
              }
            }}
            aria-label={t('mac.searchPlaceholder')}
          />
        </label>
        {compact ? null : (
          <button
            className="icon-button"
            title={t('mac.newMenu')}
            aria-label={t('mac.newMenu')}
            aria-haspopup="menu"
            onClick={(e) => {
              e.stopPropagation();
              setNewMenu(e.currentTarget.getBoundingClientRect());
            }}
          >
            <Icon name="plusCircle" size={19} />
          </button>
        )}
      </div>
      {isSearching ? (
        <button
          className="search-messages-row"
          onClick={() => {
            props.onSearchMessages(props.query.trim());
            props.onQuery('');
          }}
        >
          <Icon name="search" size={14} />
          {tf('web.nav.searchMessages', props.query.trim())}
        </button>
      ) : null}
      {/* The list sits ON a raised, round-topped sheet (the phones' and the Mac's
          ChannelListSheetBackground), chips and segment included. */}
      <div className="list-sheet">
      {splitsList ? (
        <div className="phone-segment" role="tablist">
          {(['dms', 'channels'] as const).map((tab) => {
            const mentions = tab === 'dms' ? tabBadges.dmsMentions : tabBadges.channelsMentions;
            const unread = tab === 'dms' ? tabBadges.dmsUnread : tabBadges.channelsUnread;
            return (
              <button key={tab} type="button" role="tab" aria-selected={phoneTab === tab} onClick={() => setPhoneTab(tab)}>
                {tab === 'dms' ? t('channelList.tab.dms') : t('channelList.lens.channels')}
                {mentions > 0 ? <span className="chip-count mention">{mentions}</span> : unread ? <span className="segment-dot" /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
      {isSearching ? null : (
      <div className="chips" role="toolbar">
        {/* «Ponerse al día» leads the row while something waits (PARITY P295, the Mac's chip). */}
        {model ? <CatchUpChip sections={model.sections} onOpenChannel={props.onSelectChannel} /> : null}
        <button className="chip" aria-pressed={unreadOnly} title={t('channelList.chip.unread')} aria-label={t('channelList.chip.unread')} onClick={() => setUnreadOnly(!unreadOnly)}>
          <span className="chip-glyph">
            <Icon name="envelope" size={16} />
            {anyUnread ? <span className="unread-dot" /> : null}
          </span>
        </button>
        {/* Hilos exists only where collapsed threads do (ThreadsAvailability: server config + the
            reader's collapsed_reply_threads) — the Mac hides the chip rather than open an empty screen. */}
        {threads.enabled ? (
          <button className="chip" aria-pressed={props.activeDestination === 'threads'} title={t('web.nav.threads')} aria-label={threads.unreadThreads > 0 ? tf('web.a11y.withUnread', t('web.nav.threads'), threads.unreadThreads) : t('web.nav.threads')} onClick={() => props.onOpenDestination('threads')}>
            <Icon name="threads" size={16} />
            {threads.unreadThreads > 0 ? <span className={threads.unreadMentions > 0 ? 'chip-count mention' : 'chip-count'} aria-hidden>{threads.unreadThreads}</span> : null}
          </button>
        ) : null}
        <button className="chip" aria-pressed={props.activeDestination === 'activity'} title={t('channelList.tab.activity')} aria-label={t('channelList.tab.activity')} onClick={() => props.onOpenDestination('activity')}>
          <Icon name="at" size={16} />
        </button>
        <button className="chip" aria-pressed={props.activeDestination === 'saved'} title={t('channelList.tab.saved')} aria-label={t('channelList.tab.saved')} onClick={() => props.onOpenDestination('saved')}>
          <Icon name="bookmark" size={16} />
        </button>
        {props.draftCount > 0 ? (
          <button className="chip" aria-pressed={props.activeDestination === 'drafts'} title={t('web.nav.drafts')} aria-label={t('web.nav.drafts')} onClick={() => props.onOpenDestination('drafts')}>
            <Icon name="pencilLine" size={16} />
            <span className="chip-count quiet">{props.draftCount}</span>
          </button>
        ) : null}
      </div>
      )}
      <div
        className="channel-list"
        onKeyDown={listboxKeys}
      >
        {!model ? null : model.firstRun ? (
          <FirstRunSkeleton />
        ) : sections.length === 0 ? (
          <div className="empty-hint">{isSearching ? t('channelList.noResults.description') : t('channelList.empty.description')}</div>
        ) : (
          <>
          <FoldedCategories sections={sections.filter((s) => folded.has(s.id))} onExpand={(id) => toggleSection(id, false)} />
          {sections.filter((s) => !folded.has(s.id)).map((section) => (
            <Section
              key={section.id}
              teamId={props.teamId}
              section={section}
              isSearching={isSearching}
              selectedId={props.selectedChannelId}
              focusId={focusId}
              now={now}
              onSelect={(id) => {
                const row = section.rows.find((r) => r.id === id);
                if (row && asksThreadOrChannel(row, isSearching)) {
                  const el = document.querySelector(`.channel-list [data-channel-id="${CSS.escape(id)}"]`);
                  setThreadChoice({ row, anchor: el?.getBoundingClientRect() ?? null });
                  return;
                }
                props.onSelectChannel(id);
                if (isSearching) props.onQuery('');
              }}
              onToggle={() => toggleSection(section.id, !section.collapsed)}
              onContext={(e, row) => {
                e.preventDefault();
                setMenu({ x: e.clientX, y: e.clientY, row });
              }}
            />
          ))}
          </>
        )}
      </div>
      </div>
      {/* Slack's place for a huddle: the foot of the sidebar, under the list, above who you are. */}
      <HuddlePanel />
      {props.notice}
      {model?.me ? (
        <div className="sidebar-footer">
          <button className="me" onClick={(e) => props.onOpenProfile(e.currentTarget.getBoundingClientRect())} title={t('mac.profileHelp')}>
            <Avatar userId={model.me.id} name={userDisplayName(model.me)} size={28} version={model.me.lastPictureUpdate} presence={model.myPresence} ring />
            <span>{userDisplayName(model.me)}</span>
            <OwnStatusEmoji user={model.me} />
          </button>
        </div>
      ) : null}
      {newMenu ? (
        <NewMenu
          whatsAppLine={whatsAppLine}
          anchor={newMenu}
          onClose={() => setNewMenu(null)}
          onPick={(kind) => {
            setNewMenu(null);
            props.onNew(kind);
          }}
        />
      ) : null}
      {threadChoice ? <ThreadOrChannelChooser choice={threadChoice} onClose={() => setThreadChoice(null)} onOpenChannel={props.onSelectChannel} /> : null}
      {menu ? (
        <div className="popover" style={{ left: Math.max(8, Math.min(menu.x, window.innerWidth - 268)), top: Math.max(8, Math.min(menu.y, window.innerHeight - 340)), position: 'fixed' }} role="menu" onClick={(e) => e.stopPropagation()}>
          {ChannelMarkRead.isOffered(menu.row.isUnread, menu.row.mentionCount, menu.row.isMuted) ? (
            <button
              className="menu-item"
              role="menuitem"
              onClick={() => {
                props.onMarkRead(menu.row.id);
                setMenu(null);
              }}
            >
              <Icon name="check" size={16} /> {t('channelList.markRead')}
            </button>
          ) : null}
          <RowMuteItem row={menu.row} onDone={() => setMenu(null)} onAskConfirm={setConfirmMute} />
          <MoveToMenuItems teamId={props.teamId} channelId={menu.row.id} onDone={() => setMenu(null)} />
          <CopyNameItems row={menu.row} onDone={() => setMenu(null)} />
        </div>
      ) : null}
      {confirmMute ? <MuteConfirm row={confirmMute} onClose={() => setConfirmMute(null)} /> : null}
    </nav>
  );
}

function FirstRunSkeleton() {
  return (
    <div aria-label={t('channelList.firstRun.message')}>
      <div className="empty-hint">{t('channelList.firstRun.message')}</div>
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="skeleton-row" style={{ opacity: 1 - i * 0.12 }} />
      ))}
    </div>
  );
}

const Section = memo(function Section({
  teamId,
  section,
  isSearching,
  selectedId,
  focusId,
  now,
  onSelect,
  onToggle,
  onContext,
}: {
  teamId: string | null;
  section: ChannelListSection;
  isSearching: boolean;
  selectedId: string | null;
  focusId: string | null;
  now: number;
  onSelect(id: string): void;
  onToggle(): void;
  onContext(e: MouseEvent, row: DisplayChannel): void;
}) {
  const rows = visibleRows(section.rows, { collapsed: section.collapsed, isSearching, selectedId });
  return (
    <div role="group" aria-label={categoryDisplayName(section.type, section.displayName)}>
      {teamId && !AreaGrouping.isAreaSectionId(section.id) && !ChannelMeta.isTypeSectionId(section.id) ? (
        // A stored category's menu (shared/API.md §41.1): right-click the header, or its "…".
        <div
          className="nav-category-row"
          onContextMenu={(e) => {
            e.preventDefault();
            openNav({ kind: 'categoryMenu', teamId, categoryId: section.id, at: { x: e.clientX, y: e.clientY } });
          }}
        >
          <h3 className="category-heading">
            <CategoryHeader section={section} onToggle={onToggle} />
          </h3>
          <button
            type="button"
            className="icon-button nav-category-more"
            title={t('web.categories.menu')}
            aria-label={t('web.categories.menu')}
            aria-haspopup="menu"
            onClick={(e) => {
              e.stopPropagation();
              const r = e.currentTarget.getBoundingClientRect();
              openNav({ kind: 'categoryMenu', teamId, categoryId: section.id, at: { x: r.left, y: r.bottom + 4 } });
            }}
          >
            <Icon name="more" size={14} />
          </button>
        </div>
      ) : (
        <h3 className="category-heading">
          <CategoryHeader section={section} onToggle={onToggle} />
        </h3>
      )}
      {/* Each category's rows are one listbox (heading + listbox per category, P255): the header and
          its «…» stay outside it, where buttons are allowed. */}
      {rows.length ? (
        <div role="listbox" aria-label={categoryDisplayName(section.type, section.displayName)}>
          {rows.map((row) => (
            <Row key={row.id} row={row} selected={row.id === selectedId} focusable={row.id === focusId} now={now} onSelect={onSelect} onContext={onContext} />
          ))}
        </div>
      ) : null}
    </div>
  );
});

const Row = memo(function Row({
  row,
  selected,
  focusable,
  now,
  onSelect,
  onContext,
}: {
  row: DisplayChannel;
  selected: boolean;
  focusable: boolean;
  now: number;
  onSelect(id: string): void;
  onContext(e: MouseEvent, row: DisplayChannel): void;
}) {
  const time = row.lastPostAt ? RelativeTime.format(Math.max(row.lastPostAt, row.previewAt), now, { yesterday: t('channelList.time.yesterday') }) : '';
  const classes = ['row'];
  if (row.isUnread && !row.isMuted) classes.push('unread');
  if (row.isMuted) classes.push('muted');
  return (
    <button
      className={classes.join(' ')}
      role="option"
      aria-selected={selected}
      aria-current={selected}
      tabIndex={focusable ? 0 : -1}
      // The click PAINTS before the conversation is built (1.0.276): selection is local state.
      onMouseDown={(e) => {
        if (e.button === 0) onSelect(row.id);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSelect(row.id);
      }}
      onContextMenu={(e) => onContext(e, row)}
      title={row.dmPartnerCustomStatusText || undefined}
      data-channel-id={row.id}
    >
      <span className="leading">
        <ConversationFace row={row} />
      </span>
      <span className="text">
        <span className="name">
          {row.name}
          {row.dmPartnerCustomStatusEmoji ? <span className="custom-status-emoji">{row.dmPartnerCustomStatusEmoji}</span> : null}
          {row.isMuted ? <Icon name="bellSlash" size={12} style={{ marginLeft: 6, verticalAlign: -1 }} title={t('channelList.muted.accessibilityLabel')} /> : null}
        </span>
        {row.preview ? (
          <span className="preview">
            {row.previewIsThreadReply ? '↳ ' : ''}
            {row.preview}
          </span>
        ) : null}
      </span>
      <span className="trailing">
        {/* A live call outranks the clock: "something is happening here NOW" is the news. */}
        <CallRowSlot channelId={row.id} channelName={row.name}>{time ? <span className="time">{time}</span> : null}</CallRowSlot>
        {row.mentionCount > 0 ? (
          <span className="badge" aria-label={tf('channel.jump.newBelow', row.mentionCount)}>
            {row.mentionCount}
          </span>
        ) : row.unreadThreadReplies > 0 && !row.isMuted ? (
          // A mention outranks unread thread replies, which outrank a plain unread dot (Mac row).
          <span className="thread-replies-badge" aria-label={tf('channelList.unreadThreadReplies.accessibilityLabel', row.unreadThreadReplies)}>
            ↳{row.unreadThreadReplies}
          </span>
        ) : row.isUnread && !row.isMuted ? (
          <span className="unread-dot-row" />
        ) : null}
      </span>
    </button>
  );
});

/** A conversation's face: the DM partner's photo + presence, a group's faces, else the channel glyph. Also the conversation header's. */
export function ConversationFace({ row }: { row: DisplayChannel }) {
  if (row.type === 'D') {
    return <Avatar userId={row.dmPartnerId ?? row.id} name={row.dmPartnerName ?? row.name} version={row.dmPartnerLastPictureUpdate} presence={row.dmPartnerPresence} size={32} ring />;
  }
  if (row.type === 'G') {
    const faces = row.groupFaces;
    if (!faces.length) return <Icon name="threads" size={18} />;
    // Up to three faces, in the order the title names them; never "+N" (VISUAL-DESIGN §5).
    const layout =
      faces.length === 1
        ? [{ x: 4, y: 4, s: 24 }]
        : faces.length === 2
          ? [
              { x: 0, y: 0, s: 22 },
              { x: 10, y: 10, s: 22 },
            ]
          : [
              { x: 7, y: 0, s: 18 },
              { x: 0, y: 14, s: 18 },
              { x: 14, y: 14, s: 18 },
            ];
    return (
      <span className="group-faces">
        {faces.slice(0, 3).map((f, i) => (
          <span key={f.userId} style={{ position: 'absolute', left: layout[i].x, top: layout[i].y }}>
            <Avatar userId={f.userId} name={f.displayName} size={layout[i].s} version={f.lastPictureUpdate} />
          </span>
        ))}
      </span>
    );
  }
  // A Core área channel reads as a team, not a topic: the people glyph (shared/API.md §37).
  if (row.isAreaChannel) return <Icon name="people" size={18} />;
  return <Icon name={row.type === 'P' ? 'lock' : 'hash'} size={17} />;
}

function NewMenu({ whatsAppLine, anchor, onClose, onPick }: { whatsAppLine: boolean; anchor: DOMRect; onClose(): void; onPick(kind: NewKind): void }) {
  useEffect(() => {
    const close = () => onClose();
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    setTimeout(() => window.addEventListener('click', close));
    window.addEventListener('keydown', esc);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('keydown', esc);
    };
  }, [onClose]);
  return (
    <div className="popover" role="menu" style={{ position: 'fixed', left: Math.max(8, anchor.right - 240), top: anchor.bottom + 6, width: 240 }} onClick={(e) => e.stopPropagation()}>
      <button className="menu-item" role="menuitem" onClick={() => onPick('newConversation')}>
        <Icon name="pencilLine" size={16} /> {t('web.nav.newMessage')}
      </button>
      {whatsAppLine ? (
        <button className="menu-item" role="menuitem" onClick={() => onPick('whatsAppChat')}>
          <Icon name="phone" size={16} /> {t('whatsappChat.menuTitle')}
        </button>
      ) : null}
      <button className="menu-item" role="menuitem" onClick={() => onPick('messageArea')}>
        <Icon name="people" size={16} /> {t('web.area.messageArea')}
      </button>
      <button className="menu-item" role="menuitem" onClick={() => onPick('browseChannels')}>
        <Icon name="search" size={16} /> {t('mac.joinChannel')}
      </button>
      <button className="menu-item" role="menuitem" onClick={() => onPick('createChannel')}>
        <Icon name="plus" size={16} /> {t('web.nav.createChannel')}
      </button>
    </div>
  );
}

/**
 * The conversation list, one listbox per category (PARITY P255): ↑/↓ move between rows ACROSS
 * categories, Home/End to the ends,
 * Enter/Space open (the row's own handler). Tab leaves the list — one stop, like the natives' lists.
 */
function listboxKeys(e: ReactKeyboardEvent<HTMLDivElement>): void {
  if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key) || e.altKey || e.metaKey || e.ctrlKey) return;
  const options = [...e.currentTarget.querySelectorAll<HTMLElement>('[role="option"]')];
  if (!options.length) return;
  const at = options.indexOf(document.activeElement as HTMLElement);
  if (at < 0 && !(document.activeElement && e.currentTarget.contains(document.activeElement))) return;
  e.preventDefault();
  const next = e.key === 'Home' ? 0 : e.key === 'End' ? options.length - 1 : Math.max(0, Math.min(options.length - 1, (at < 0 ? -1 : at) + (e.key === 'ArrowDown' ? 1 : -1)));
  for (const o of options) o.tabIndex = -1;
  options[next].tabIndex = 0;
  options[next].focus();
}
